import { useEffect, useState } from 'react';

/**
 * How far the reader scrolls before the hint has done its job.
 *
 * Deliberately small. The hint answers one question — does this move? — and
 * once it has been answered, leaving it on the screen for the rest of a long
 * form is nagging rather than helping.
 */
const ENGAGED_AFTER_PX = 24;

/**
 * Whether a scroll container has content left below the fold.
 *
 * Asks the question geometrically rather than arithmetically: a zero-height
 * sentinel sits at the end of the content, and the browser reports whether it
 * is in view. Whatever moved it — a disclosure opening, a step swapping, the
 * dialog settling out of its open animation, fonts landing — the answer
 * updates without anything having to predict the cause.
 *
 * The arithmetic it replaces (`scrollHeight - clientHeight - scrollTop`) had to
 * enumerate its own re-measure triggers, so any path nobody thought of went
 * stale; a container stretched by `flex-1` resizes for none of its content, and
 * the hint then reported whatever the first frame happened to see. It also
 * counted the container's own bottom padding as content, which needed a
 * threshold to discount. The sentinel sits inside that padding, so there is
 * nothing to discount.
 *
 * Both refs are callbacks rather than objects: a drawer mounts its content
 * after the hook, and an effect reading a ref that was null on mount would
 * never see it.
 */
export const useScrollOverflow = <T extends HTMLElement>(
    /**
     * Marks a fresh step inside one container. The engaged latch answers "does
     * this step move?", so a multi-step surface passes its step key here: each
     * change rewinds the scroll and re-arms the hint, which would otherwise
     * stay answered for the rest of the journey after the first scroll.
     */
    resetKey?: unknown,
) => {
    const [scrollEl, setScrollEl] = useState<T | null>(null);
    const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [engaged, setEngaged] = useState(false);

    useEffect(() => {
        if (!scrollEl || !sentinelEl) return;
        const observer = new IntersectionObserver(
            ([entry]) => setHasMore(!entry?.isIntersecting),
            { root: scrollEl },
        );
        observer.observe(sentinelEl);
        return () => observer.disconnect();
    }, [scrollEl, sentinelEl]);

    useEffect(() => {
        if (!scrollEl) return;
        const onScroll = () => {
            // Latched: scrolling back to the top does not re-ask a question the
            // reader has already answered for this step.
            if (scrollEl.scrollTop > ENGAGED_AFTER_PX) setEngaged(true);
        };
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [scrollEl]);

    useEffect(() => {
        if (resetKey === undefined) return;
        setEngaged(false);
        // The sentinel reports the new step's overflow on its own; only the
        // reader's position and the latch belong to the step that just left.
        if (scrollEl) scrollEl.scrollTop = 0;
    }, [resetKey, scrollEl]);

    return {
        /** Attach to the scroll container. */
        ref: setScrollEl,
        /** Attach to an empty element rendered last inside that container. */
        sentinelRef: setSentinelEl,
        hasMore,
        /**
         * Show the hint: there is more below, and the reader has not yet shown
         * they know it moves.
         */
        showHint: hasMore && !engaged,
    };
};
